import { useState, type ComponentProps, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./Button";

/**
 * A button for an action that cannot be undone (a server-side delete): the first press asks in place — the
 * question, *Yes* and *Cancel* — and only *Yes* acts. The one way to confirm in the app (UI cleanup U2: these used
 * the browser's own dialog, while changes in the workspace offer an undo toast instead).
 */
export function ConfirmButton({
  question,
  onConfirm,
  children,
  testId,
  ...props
}: Omit<ComponentProps<typeof Button>, "onClick"> & {
  question: string;
  onConfirm(): void;
  children: ReactNode;
  testId?: string;
}) {
  const { t } = useTranslation();
  const [asking, setAsking] = useState(false);
  if (!asking)
    return (
      <Button {...props} onClick={() => setAsking(true)} data-testid={testId}>
        {children}
      </Button>
    );
  return (
    <span className="inline-flex flex-wrap items-center gap-2" role="group" aria-label={question}>
      <span className="text-[12.5px] text-neutral-700 dark:text-neutral-200">{question}</span>
      <Button
        size="sm"
        variant="danger"
        autoFocus
        onClick={() => {
          setAsking(false);
          onConfirm();
        }}
        data-testid={testId ? `${testId}-confirm` : undefined}
      >
        {t("common.yes")}
      </Button>
      <Button size="sm" variant="secondary" onClick={() => setAsking(false)}>
        {t("common.cancel")}
      </Button>
    </span>
  );
}
